// Template edits, per logged-in user, on this device. Kept in localStorage (the
// same backend-free posture as recentDestinations.ts / apiKey.ts) so a user's own
// templates and their tweaks to the built-in ones survive a reload and every
// later session — until templates move server-side and become admin-editable.
//
// Only what the user actually changed is persisted: their own templates in full,
// plus built-ins whose fields differ from the shipped defaults. Untouched
// built-ins keep following the code, so improving a default prompt in a release
// reaches everyone instead of being shadowed forever by a stored copy.

import { MagicTemplate, defaultTemplates } from './model';

const STORAGE_PREFIX = 'assetLab.magicStudio.templates.v1';

function keyFor(userId: string): string {
  return `${STORAGE_PREFIX}.${userId || 'anon'}`;
}

function isTemplate(value: unknown): value is MagicTemplate {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.id === 'string' &&
    typeof t.label === 'string' &&
    typeof t.hint === 'string' &&
    typeof t.prompt === 'string'
  );
}

// The user-editable fields, as a comparable string — what decides whether a
// built-in counts as "touched" and therefore has to be stored.
function signature(tpl: MagicTemplate): string {
  return JSON.stringify([
    tpl.label,
    tpl.hint,
    tpl.prompt,
    !!tpl.postUpscale,
    !!tpl.postRemoveBg,
    tpl.presetRefs ?? [],
  ]);
}

// The user's templates: the built-ins (in their shipped order) with any stored
// edits applied, followed by the user's own templates. Falls back to the plain
// defaults when storage is unavailable or holds nothing valid.
export function loadTemplates(userId: string): MagicTemplate[] {
  const defaults = defaultTemplates();
  let stored: MagicTemplate[] = [];
  try {
    const raw = localStorage.getItem(keyFor(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) stored = parsed.filter(isTemplate);
  } catch {
    // Unavailable (private mode) or corrupted — ship the defaults.
    return defaults;
  }
  if (!stored.length) return defaults;

  const byId = new Map(stored.map((t) => [t.id, t]));
  const builtinIds = new Set(defaults.map((t) => t.id));
  // A stored built-in overrides its default; `custom: false` keeps it
  // undeletable even if an older record claimed otherwise.
  const builtins = defaults.map((d) => {
    const saved = byId.get(d.id);
    return saved ? { ...d, ...saved, custom: false } : d;
  });
  // Templates the user created. A record whose id has since become a built-in
  // (or a duplicate) is dropped rather than shown twice.
  const own = stored.filter((t) => !builtinIds.has(t.id));
  return [...builtins, ...own];
}

// Persist the user's templates. Best-effort: remembering them is a convenience,
// so a full / unavailable storage is silently tolerated.
export function saveTemplates(userId: string, templates: MagicTemplate[]): void {
  const defaults = new Map(defaultTemplates().map((t) => [t.id, t]));
  const changed = templates.filter((t) => {
    const base = defaults.get(t.id);
    return !base || signature(t) !== signature(base);
  });
  try {
    if (changed.length) {
      localStorage.setItem(keyFor(userId), JSON.stringify(changed));
    } else {
      // Everything is back to the defaults — drop the record entirely so the
      // templates keep tracking the code.
      localStorage.removeItem(keyFor(userId));
    }
  } catch {
    // ignore — nothing else we can do if storage is unavailable.
  }
}
